import React, { useState, useEffect } from "react";
import { api, apiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { TAMANOS, normContenedor } from "@/lib/etapas";
import { TipoTag } from "@/components/marca/Pills";
import { ContainerId } from "@/components/marca/ContainerId";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { t } from "@/lib/i18n";

const CONTENEDOR_RE = /^[A-Z]{4}[0-9]{7}$/;

// Fix P (2026-10-13): datos generales de la carga (ID de carga, tipo, tamaño, contenedor asignado, cliente y notas).
// Los usan la pantalla «Nueva carga» y el expediente. `datos` y `onChange(campo, valor)` viven en la pantalla que lo usa.
export function DatosCarga({ datos, onChange, clientes, onClienteCreado, cargaId, onIdExiste, exportNuevo = false }) {
  const [idAviso, setIdAviso] = useState(false);
  const [dupAviso, setDupAviso] = useState(null);
  const [nuevoClienteOpen, setNuevoClienteOpen] = useState(false);

  const contNorm = normContenedor(datos.contenedor);
  const formatoRaro = contNorm.length > 0 && !CONTENEDOR_RE.test(contNorm);

  useEffect(() => { if (onIdExiste) onIdExiste(idAviso); }, [idAviso, onIdExiste]);

  const verificarId = async () => {
    const v = (datos.load_id || "").trim();
    if (!v) { setIdAviso(false); return; }
    try {
      const { data } = await api.get(`/cargas/verificar-id?load_id=${encodeURIComponent(v)}${cargaId ? t("&excluir={cargaId}", { cargaId }) : ""}`);
      setIdAviso(!!data.existe);
    } catch { setIdAviso(false); }
  };

  const verificarDup = async () => {
    if (cargaId || !contNorm) return;
    try {
      const { data } = await api.get(`/cargas/verificar-contenedor?numero=${encodeURIComponent(contNorm)}`);
      setDupAviso(data.duplicado ? data.fecha : null);
    } catch { setDupAviso(null); }
  };

  return (
    <div className="space-y-4" data-testid="datos-carga">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>{t("ID de carga *")}</Label>
          <Input
            data-testid="carga-loadid-input"
            value={datos.load_id}
            onChange={(e) => { onChange("load_id", e.target.value.toUpperCase()); setIdAviso(false); }}
            onBlur={verificarId}
            placeholder={t("Ej.: LG-1042")}
            maxLength={40}
            className="mt-1.5 h-11 font-mono"
          />
          {idAviso && <p data-testid="aviso-id-existe" className="text-xs text-danger font-semibold mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{t(" Ya existe una carga con este ID de carga")}</p>}
        </div>
        {exportNuevo ? (
          <div data-testid="contenedor-export-aviso">
            <Label>{t("Contenedor")}</Label>
            <p className="mt-1.5 text-[13.5px] text-ink-2 bg-surface-2 border border-line rounded-md p-2.5">{t("En una exportación el chofer informa el número y la foto del contenedor en el paso 2 (Empty Container Pickup).")}{datos.contenedor ? <span className="block mt-1 font-mono text-ink">{datos.contenedor}</span> : null}</p>
          </div>
        ) : (
        <div>
          <Label>{t("Contenedor asignado *")}</Label>
          <Input
            data-testid="carga-contenedor-input"
            value={datos.contenedor}
            onChange={(e) => { onChange("contenedor", e.target.value.toUpperCase()); setDupAviso(null); }}
            onBlur={verificarDup}
            className="mt-1.5 h-11 font-mono uppercase"
          />
          {datos.contenedor && <div className="mt-2 text-ink" data-testid="contenedor-vista-previa"><ContainerId numero={contNorm} size="md" /></div>}
          {formatoRaro && <p data-testid="aviso-formato" className="text-xs text-[#B07A12] mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{t(" Formato poco común, ¿seguro?")}</p>}
          {dupAviso && <p data-testid="aviso-duplicado" className="text-xs text-[#B07A12] mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{t(" Ya existe una carga con este contenedor (fecha {dupAviso})", { dupAviso })}</p>}
        </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>{t("Tipo *")}</Label>
          <div className="grid grid-cols-2 gap-2 mt-1.5">
            {["importacion", "exportacion"].map((tt) => (
              <button
                key={tt}
                type="button"
                data-testid={`tipo-btn-${tt}`}
                onClick={() => onChange("tipo", tt)}
                aria-pressed={datos.tipo === tt}
                className={cn("h-11 rounded-lg border-2 flex items-center justify-center transition-colors duration-150", datos.tipo === tt ? "border-transparent" : "border-line-strong bg-white hover:bg-surface-2")}
              >
                <TipoTag tipo={tt} variant={datos.tipo === tt ? "solid" : "soft"} className={datos.tipo === tt ? "" : "bg-transparent"} />
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label>{t("Tamaño *")}</Label>
          <div className="grid grid-cols-5 gap-1.5 mt-1.5" role="group" aria-label={t("Tamaño del contenedor")}>
            {TAMANOS.map((tt) => (
              <button
                key={tt}
                type="button"
                data-testid={`tamano-btn-${tt}`}
                onClick={() => onChange("tamano", tt)}
                aria-pressed={datos.tamano === tt}
                className={cn("h-11 rounded-lg border-2 font-display font-bold text-[15px] transition-colors duration-150", datos.tamano === tt ? "bg-navy text-white border-navy" : "border-line-strong bg-white text-ink hover:bg-surface-2")}
              >{tt}</button>
            ))}
          </div>
        </div>
      </div>

      <div>
        <Label>{t("Cliente *")}</Label>
        <div className="flex gap-2 mt-1.5">
          <Select value={datos.cliente_id} onValueChange={(v) => onChange("cliente_id", v)}>
            <SelectTrigger data-testid="carga-cliente-select" className="h-11 bg-white"><SelectValue placeholder={t("Elegir cliente")} /></SelectTrigger>
            <SelectContent className="bg-white">
              {clientes.map((c) => <SelectItem key={c.id} value={c.id}>{c.frecuente ? "★ " : ""}{c.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button type="button" data-testid="carga-nuevo-cliente-btn" variant="outline" className="h-11 whitespace-nowrap" onClick={() => setNuevoClienteOpen(true)}>{t("+ Nuevo cliente")}</Button>
        </div>
      </div>

      <div>
        <Label>{t("Notas")}</Label>
        <Textarea data-testid="carga-notas-input" value={datos.notas} onChange={(e) => onChange("notas", e.target.value)} className="mt-1.5" rows={2} />
      </div>

      {nuevoClienteOpen && (
        <NuevoClienteDialog
          onClose={() => setNuevoClienteOpen(false)}
          onCreated={(cli) => { onClienteCreado(cli); setNuevoClienteOpen(false); }}
        />
      )}
    </div>
  );
}

function NuevoClienteDialog({ onClose, onCreated }) {
  const [nombre, setNombre] = useState("");
  const [direccion, setDireccion] = useState("");
  const [frecuente, setFrecuente] = useState(false);
  const [saving, setSaving] = useState(false);

  const guardar = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/clientes", { nombre, direccion, frecuente, notas: "", activo: true });
      toast.success(t("Cliente creado"));
      onCreated(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle>{t("Nuevo cliente")}</DialogTitle></DialogHeader>
        <form onSubmit={guardar} className="space-y-4">
          <div>
            <Label className="eyebrow">{t("Nombre *")}</Label>
            <Input data-testid="nuevo-cliente-nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} className="mt-1 h-11" required />
          </div>
          <div>
            <Label className="eyebrow">{t("Dirección")}</Label>
            <Input value={direccion} onChange={(e) => setDireccion(e.target.value)} className="mt-1 h-11" />
          </div>
          <label className="flex items-center gap-2 text-sm text-ink-2">
            <input type="checkbox" checked={frecuente} onChange={(e) => setFrecuente(e.target.checked)} className="h-4 w-4" />{t(" Cliente frecuente")}</label>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>{t("Cancelar")}</Button>
            <Button type="submit" data-testid="nuevo-cliente-guardar" className="bg-brand hover:bg-brand-dark text-white" disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Crear")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
